import { FC, Fragment } from 'react';

import { EmbeddedMessageMedium, MessageMedium, WhatsAppMedium } from 'constants/messageMediums';
import { BaseAssetManagerContextProvider } from 'modules/assetManager/contexts/BaseAssetManagerContextProvider';
import { displayContent } from 'modules/templateEditor/common/content';
import { ModalProvider } from 'modules/templateEditor/context';
import { CampaignQueryContextProvider } from 'modules/templateEditor/context/CampaignQueryContextProvider';
import { EditableFieldsContextProvider } from 'modules/templateEditor/context/EditableFieldsContextProvider';
import { EditorFormProvider } from 'modules/templateEditor/context/EditorFormProvider';
import { EditSavedRowDesignProvider } from 'modules/templateEditor/context/EditSavedRowDesignContext';
import { ErrorValidationContextProvider } from 'modules/templateEditor/context/ErrorValidationContext';
import { MessageTypesQueryContextProvider } from 'modules/templateEditor/context/MessageTypesQueryContextProvider';
import { PreviewWithDataContextProvider } from 'modules/templateEditor/context/PreviewWithDataContextProvider';
import { SendTestMsgContextProvider } from 'modules/templateEditor/context/SendTestMsgContextProvider';
import { TemplateQueryContextProvider } from 'modules/templateEditor/context/TemplateQueryContextProvider';
import { EditEmailContext } from 'modules/templateEditor/EditEmail/context/EditMailContext';
import { EmbeddedContext } from 'modules/templateEditor/EditEmbedded/context/EmbeddedContext';
import { InAppContext } from 'modules/templateEditor/EditInApp/context/InAppContext';
import { PushContext } from 'modules/templateEditor/EditPush/context/PushContext';
import { SMSContext } from 'modules/templateEditor/EditSMS/context/SMSContext';
import { WebPushContext } from 'modules/templateEditor/EditWebPush/context/WebPushContext';
import { WhatsAppContext } from 'modules/templateEditor/EditWhatsApp/context/WhatsAppContext';
import {
  useCreateSmartOptInLink,
  useSmartOptInLinksByProjectId,
  useUpdateSmartOptInLink,
} from 'modules/templateEditor/hooks/useSmartOptInLinks';
import { Modals } from 'modules/templateEditor/modals';
import { SmsSmartOptInProvider } from 'modules/templateEditor/modals/SmsSmartOptInModal/contexts/SmsSmartOptInProvider';
import { validateSmartLink } from 'modules/templateEditor/modals/SmsSmartOptInModal/SmsSmartOptInModal.utils';
import { JsonViewModeContextProvider, ViewTypes } from 'ui/treeView';
import { LocaleSettingsContextProvider } from 'modules/campaigns/lib/contexts/LocaleSettingsContext';
import { LocalesQueryContextProvider } from 'modules/campaigns/lib/contexts/LocalesQueryContext';
import { ModalContextProvider } from 'utils/contexts/modal';
import { TemplateStateContextProvider } from '../context/TemplateStateContextProvider';
import { InStudioTakeoverContextProvider } from './context/InStudioTakeoverContextProvider';
import { InStudioTemplateProps } from './InStudioTemplateEditing.types';

export const CampaignTemplateProvider: FC<React.PropsWithChildren<InStudioTemplateProps>> = ({
  children,
  ...props
}) => (
  <InStudioTakeoverContextProvider {...props}>
    <BaseAssetManagerContextProvider
      assetSource={useSmartOptInLinksByProjectId}
      assetTypeName={displayContent.strSmsSmartOptInAssetTypeName}
      validateAsset={validateSmartLink}
      createAssetHook={useCreateSmartOptInLink}
      updateAssetHook={useUpdateSmartOptInLink}
    >
      <SmsSmartOptInProvider>{children}</SmsSmartOptInProvider>
    </BaseAssetManagerContextProvider>
  </InStudioTakeoverContextProvider>
);

export const TEMPLATE_EDITOR_MEDIUM_COMPONENT_MAPPER = {
  [MessageMedium.Email]: EditEmailContext,
  [MessageMedium.InApp]: InAppContext,
  [MessageMedium.Push]: PushContext,
  [MessageMedium.SMS]: SMSContext,
  [MessageMedium.WebPush]: WebPushContext,
  [EmbeddedMessageMedium.Embedded]: EmbeddedContext,
  [WhatsAppMedium.WhatsApp]: WhatsAppContext,
} as const;

export const InStudioTemplateContext: FC<React.PropsWithChildren<InStudioTemplateProps>> = ({
  children,
  ...props
}) => {
  const { messageMedium = '' } = props;
  const MediumContext =
    TEMPLATE_EDITOR_MEDIUM_COMPONENT_MAPPER[
      messageMedium as keyof typeof TEMPLATE_EDITOR_MEDIUM_COMPONENT_MAPPER
    ] ?? Fragment;

  return (
    <TemplateStateContextProvider {...props}>
      <LocaleSettingsContextProvider>
        <LocalesQueryContextProvider>
          <CampaignQueryContextProvider>
            <TemplateQueryContextProvider>
              <EditorFormProvider>
                <ErrorValidationContextProvider>
                  <MessageTypesQueryContextProvider>
                    <EditableFieldsContextProvider>
                      <PreviewWithDataContextProvider>
                        <MediumContext>
                          {/*
                           * `ModalContextProvider` usage in here has been  deprecated and will be removed in the future,
                           * please use `ModalProvider`for all new modal management features
                           * @see src/modules/templateEditor/context/ModalContext.ts
                           */}
                          <ModalContextProvider>
                            <ModalProvider>
                              <SendTestMsgContextProvider>
                                <JsonViewModeContextProvider
                                  identifier="raw-data"
                                  defaultMode={ViewTypes.raw}
                                >
                                  <InStudioTakeoverContextProvider {...props}>
                                    <EditSavedRowDesignProvider>
                                      {children}
                                    </EditSavedRowDesignProvider>
                                  </InStudioTakeoverContextProvider>
                                  <Modals />
                                </JsonViewModeContextProvider>
                              </SendTestMsgContextProvider>
                            </ModalProvider>
                          </ModalContextProvider>
                        </MediumContext>
                      </PreviewWithDataContextProvider>
                    </EditableFieldsContextProvider>
                  </MessageTypesQueryContextProvider>
                </ErrorValidationContextProvider>
              </EditorFormProvider>
            </TemplateQueryContextProvider>
          </CampaignQueryContextProvider>
        </LocalesQueryContextProvider>
      </LocaleSettingsContextProvider>
    </TemplateStateContextProvider>
  );
};
